import $ from 'jquery';
import {register} from '@shopify/theme-sections';
import {trapFocus, removeTrapFocus} from '@shopify/theme-a11y';
import {buildCookieString, parseCookie} from '../utils/cookies';

const selectors = {
  mobileMenuToggle: '[data-mobile-menu-toggle]',
  submenuToggle: '[data-submenu-toggle]',
  menu: '[data-main-menu]',
  searchToggle: '[data-search-toggle]',
  searchForm: '[data-search-form]',
  stickyContainer: '[data-header-sticky]',
};

const cookieUtils = {
  set(name, value, days) {
    document.cookie = buildCookieString(name, value, days);
  },
  get(name) {
    return parseCookie(name, document.cookie);
  },
};

/**
 * Testimonials section constructor. Runs on page load as well as Theme Editor
 * `section:load` events.
 * @param {string} container - selector for the section container DOM element
 */

register('header', {
  onLoad() {
    this.$container = $(this.container);
    this.namespace = `.${this.id}`;
    this.triggerElement = null;
    this._onMobileMenuKeydown = (e) => {
      if (e.key === 'Escape') this.closeMobileMenu();
    };

    this.initMobileMenu();
    this.initStickyHeader();
    this.initBackroomModal();
  },

  initStickyHeader() {
    const stickyContainer = $(selectors.stickyContainer, this.$container);
    let offset = stickyContainer.offset();
    let scrollTicking = false;

    const redraw = () => {
      const scrollTop = $(window).scrollTop();

      if (scrollTop > offset.top && !stickyContainer.hasClass('state--sticky')) {
        this.$container.css({
          paddingBottom: stickyContainer.height(),
        });
        stickyContainer.addClass('state--sticky');
      } else if (scrollTop <= offset.top) {
        stickyContainer.removeClass('state--sticky');
        this.$container.css({
          paddingBottom: '',
        });
      }
    };

    this._onScroll = () => {
      if (!scrollTicking) {
        requestAnimationFrame(() => {
          redraw();
          scrollTicking = false;
        });
        scrollTicking = true;
      }
    };

    this._resizeTimer = null;
    this._onResize = () => {
      clearTimeout(this._resizeTimer);
      this._resizeTimer = setTimeout(() => {
        const isSticky = stickyContainer.hasClass('state--sticky');
        if (isSticky) {
          stickyContainer.removeClass('state--sticky');
          this.$container.css({
            paddingBottom: '',
          });
        }
        offset = stickyContainer.offset();
        redraw();
      }, 150);
    };

    $(window).on(`scroll${this.namespace}`, this._onScroll);
    $(window).on(`resize${this.namespace}`, this._onResize);
  },

  initMobileMenu() {
    $('[data-mobile-menu]', this.$container).addClass('initialized');

    $(selectors.mobileMenuToggle, this.$container).on('click', (e) => {
      e.preventDefault();

      if ($('[data-mobile-menu]', this.$container).hasClass('opened')) {
        this.closeMobileMenu();
      } else {
        this.openMobileMenu();
      }
    });

    $(selectors.submenuToggle, this.$container).on('click', (e) => {
      e.preventDefault();
      if ($(window).width() <= 1024) {
        const root = $(e.currentTarget).closest('[data-submenu-root]');
        root.toggleClass('opened').find('> [data-submenu]').slideToggle(150);
        const isOpen = root.hasClass('opened');
        $(e.currentTarget).attr('aria-expanded', String(isOpen));
      }
    });

    $(selectors.searchToggle, this.$container).on('click', () => {
      if ($('[data-mobile-menu]', this.$container).hasClass('opened')) {
        this.closeMobileMenu();
      }
      const searchVisible = $(selectors.searchForm, this.$container).is(':visible');
      $(selectors.searchToggle, this.$container).attr('aria-expanded', String(!searchVisible));
      $(selectors.searchForm, this.$container).slideToggle(150);
    });
  },

  openMobileMenu() {
    this.triggerElement = document.activeElement;
    $(selectors.mobileMenuToggle, this.$container).addClass('opened').attr('aria-expanded', 'true');
    $('[data-mobile-menu]', this.$container).addClass('opened');
    $(selectors.searchForm, this.$container).slideUp(150);
    $('body').css({
      overflow: 'hidden',
      paddingRight: `${this.getScrollbarWidth()}px`,
    });
    trapFocus($('[data-mobile-menu]', this.$container)[0]);
    document.addEventListener('keydown', this._onMobileMenuKeydown);
  },

  closeMobileMenu() {
    removeTrapFocus();
    $(selectors.mobileMenuToggle, this.$container).removeClass('opened').attr('aria-expanded', 'false');
    $('[data-mobile-menu]', this.$container).removeClass('opened');
    $(selectors.searchForm, this.$container).slideUp(150);
    $('body').css({
      overflow: '',
      paddingRight: '',
    });
    document.removeEventListener('keydown', this._onMobileMenuKeydown);
    if (this.triggerElement && document.body.contains(this.triggerElement)) {
      this.triggerElement.focus();
    } else {
      document.body.focus();
    }
    this.triggerElement = null;
  },

  getScrollbarWidth() {
    return window.innerWidth - document.documentElement.clientWidth;
  },

  initBackroomModal() {
    const modal = document.getElementById('backroom-modal');
    if (!modal) return;

    const cookieName = 'backroom_modal_dismissed';
    if (cookieUtils.get(cookieName)) return;

    const previousFocus = document.activeElement;

    const close = () => {
      modal.querySelector('.modal').classList.remove('modal--opened');
      document.removeEventListener('keydown', onKeydown);
      removeTrapFocus();
      setTimeout(() => { modal.style.display = 'none'; }, 300);
      cookieUtils.set(cookieName, 'true', 7);
      if (previousFocus && document.body.contains(previousFocus)) {
        previousFocus.focus();
      }
    };

    const onKeydown = (e) => {
      if (e.key === 'Escape') close();
    };

    modal.style.display = 'block';
    requestAnimationFrame(() => {
      modal.querySelector('.modal').classList.add('modal--opened');
      trapFocus(modal);
      document.addEventListener('keydown', onKeydown);
    });

    modal.querySelectorAll('[data-backroom-modal-close]').forEach((el) => {
      el.addEventListener('click', close);
    });
  },

  onUnload() {
    this.$container.off(this.namespace);
    $(window).off(this.namespace);
    clearTimeout(this._resizeTimer);
    document.removeEventListener('keydown', this._onMobileMenuKeydown);
    removeTrapFocus();
  },
});
